import React, { useState, useEffect, useContext } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { toast } from "react-toastify";
import axios from 'axios';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { UserContext } from '../../contexts/UserContext';

const API_BASE_URL = `${process.env.REACT_APP_API_URL.replace(/\/$/, '')}/api`;

// Custom styles for react-select with OEM brand colors
const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    fontSize: '14px',
    border: '1px solid #D9D9D9',
    borderRadius: '0.75rem',
    boxShadow: state.isFocused ? '0 0 0 1px #FE3F06' : 'none',
    '&:hover': { borderColor: '#FE3F06' },
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
    borderRadius: '0.75rem',
    overflow: 'hidden',
  }),
  option: (provided, state) => ({
    ...provided,
    fontSize: '14px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    backgroundColor: state.isSelected ? '#FE3F06' : state.isFocused ? '#FEE2E2' : 'white',
    color: state.isSelected ? 'white' : '#374151',
    '&:active': { backgroundColor: '#FE3F06', color: 'white' },
  }),
  singleValue: (provided) => ({
    ...provided,
    color: '#374151',
  }),
};

const OrderCreation = ({ isOpen, onClose, refreshPreOrders, data }) => {
  const { user } = useContext(UserContext);

  // Inicjalizacja orderData z danymi z przekazanego obiektu data (dla edycji) lub domyślne
  const initialOrderData = data
    ? {
        orderNumber: data.order_number || '',
        workPermitNumber: data.work_permit_number || '',
        turbineId: data.turbine || '',
        scopeOfWorkId: data.work_scope || '',
        workScopeReal: data.work_scope_real || '',
        comment: data.comment || '',
        companyToId: data.company_to || '',
      }
    : {
        orderNumber: '',
        workPermitNumber: '',
        turbineId: '',
        scopeOfWorkId: '',
        workScopeReal: '',
        comment: '',
        companyToId: '',
      };

  const [orderData, setOrderData] = useState(initialOrderData);
  const [allTurbines, setAllTurbines] = useState([]);
  const [allScopesOfWork, setAllScopesOfWork] = useState([]);
  const [filteredTurbines, setFilteredTurbines] = useState([]);
  const [filteredScopesOfWork, setFilteredScopesOfWork] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [otherScopeId, setOtherScopeId] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);

  // Pobieranie danych początkowych
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoading(true);
        setErrorMessage('');
        const token = localStorage.getItem('access_token');
        if (!token) throw new Error('No authentication token found');

        const config = {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        };

        const [turbinesResponse, scopesResponse, companiesResponse] = await Promise.all([
          axios.get(`${API_BASE_URL}/turbines/`, config),
          axios.get(`${API_BASE_URL}/scopeofworks/`, config),
          axios.get(`${API_BASE_URL}/company/`, config),
        ]);

        const turbinesData = turbinesResponse.data || [];
        const scopesData = scopesResponse.data || [];
        const companiesData = (companiesResponse.data || []).filter(company => company.is_ecc === true);

        setAllTurbines(turbinesData);
        setAllScopesOfWork(scopesData);
        setCompanies(companiesData);

        // Ustawienie otherScopeId
        const otherScope = scopesData.find(scope => scope.name === 'Other' && scope.company === orderData.companyToId);
        setOtherScopeId(otherScope ? otherScope.id : null);
      } catch (error) {
        console.error('Error fetching data:', error);
        setErrorMessage('Failed to load data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    if (isOpen) fetchInitialData();
  }, [isOpen, orderData.companyToId]);

  // Resetowanie stanu przy otwarciu
  useEffect(() => {
    if (isOpen) {
      setOrderData(initialOrderData);
    }
  }, [isOpen, data]);

  // Filtrowanie turbin i zakresów pracy po zmianie companyToId
  useEffect(() => {
    if (orderData.companyToId) {
      const filteredTurbinesData = allTurbines.filter(turbine => turbine.company === orderData.companyToId);
      const filteredScopesData = allScopesOfWork.filter(scope => scope.company === orderData.companyToId);

      setFilteredTurbines(filteredTurbinesData);
      setFilteredScopesOfWork(filteredScopesData);

      // Aktualizacja otherScopeId po zmianie firmy
      const otherScope = allScopesOfWork.find(scope => scope.name === 'Other' && scope.company === orderData.companyToId);
      setOtherScopeId(otherScope ? otherScope.id : null);
    } else {
      setFilteredTurbines([]);
      setFilteredScopesOfWork([]);
      setOtherScopeId(null);
    }
  }, [orderData.companyToId, allTurbines, allScopesOfWork]);

  // Opcje dla Select
  const companyOptions = companies.map(company => ({
    value: company.id,
    label: company.name,
  }));

  const turbineOptions = filteredTurbines.map(turbine => ({
    value: turbine.id,
    label: `${turbine.turbine_number} (${turbine.model_data?.name || 'N/A'})`,
  }));

  const scopeOptions = filteredScopesOfWork
    .filter(scope => scope.name !== 'Other')
    .map(scope => ({
      value: scope.id,
      label: scope.name,
    }));

  // Obsługa zmiany companyToId
  const handleCompanyChange = (selectedOption) => {
    setOrderData({
      ...initialOrderData,
      companyToId: selectedOption ? selectedOption.value : '',
    });
  };

  // Obsługa ręcznej zmiany pól
  const handleManualInputChange = (field, value) => {
    if (field === 'scopeOfWorkId') {
      const selectedOption = value; // value jest obiektem z CreatableSelect
      if (selectedOption && selectedOption.__isNew__) {
        setOrderData(prev => ({
          ...prev,
          scopeOfWorkId: otherScopeId || 'custom',
          workScopeReal: selectedOption.label,
        }));
      } else {
        setOrderData(prev => ({
          ...prev,
          scopeOfWorkId: selectedOption ? selectedOption.value : '',
          workScopeReal: '',
        }));
      }
    } else if (typeof value === 'object' && value !== null) {
      setOrderData(prev => ({
        ...prev,
        [field]: value.value,
      }));
    } else {
      setOrderData(prev => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  // Reset formularza
  const handleCancel = () => {
    setOrderData(initialOrderData);
    onClose();
  };

  // Sprawdzanie, czy można zapisać Order
  const canCreateOrder = orderData.companyToId && orderData.orderNumber && orderData.turbineId && orderData.scopeOfWorkId;

  // Wysyłanie formularza
  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');

    const token = localStorage.getItem('access_token');
    if (!token) {
      setErrorMessage('No authentication token found.');
      return;
    }

    const config = {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    };

    const submitData = {
      order_number: orderData.orderNumber,
      work_permit_number: orderData.workPermitNumber || null,
      turbine: orderData.turbineId,
      work_scope: orderData.scopeOfWorkId === 'custom' ? otherScopeId : orderData.scopeOfWorkId,
      work_scope_real: orderData.workScopeReal || null,
      comment: orderData.comment || null,
      status: data ? data.status : 'pending', // Zachowaj oryginalny status przy edycji
      company_to: orderData.companyToId,
      company: user?.company,
      // Zachowaj team i daty planowania przy edycji
      ...(data && data.team && { team: data.team }),
      ...(data && data.planned_date_start && { planned_date_start: data.planned_date_start }),
      ...(data && data.planned_date_end && { planned_date_end: data.planned_date_end }),
    };

    if (orderData.scopeOfWorkId === 'custom' && !otherScopeId) {
      setErrorMessage('Custom scope is not supported because "Other" option is not available for the selected company.');
      return;
    }

    if (submitData.work_scope === otherScopeId && !submitData.work_scope_real) {
      setErrorMessage('Please provide a description for the custom scope of work.');
      return;
    }

    try {
      if (canCreateOrder) {
        if (!data && user?.company) {
          const response = await axios.get(`${API_BASE_URL}/workpermit/`, config);
          const workPermits = response.data || [];
          if (workPermits.some(permit => permit.order_number === submitData.order_number && permit.company === user.company)) {
            setErrorMessage('Order number already exists for your company.');
            return;
          }
          if (submitData.work_permit_number && workPermits.some(permit => permit.work_permit_number === submitData.work_permit_number && permit.company === user.company)) {
            setErrorMessage('Work permit number already exists for your company.');
            return;
          }
        }

        if (data) {
          // Tryb edycji
          await axios.patch(`${API_BASE_URL}/workpermit/${data.id}/`, submitData, config);
          toast.success('Order updated successfully.');
        } else {
          // Tryb tworzenia
          await axios.post(`${API_BASE_URL}/workpermit/`, submitData, config);
          toast.success('Order created successfully.');
        }

        await refreshPreOrders();
        handleCancel();
      } else {
        setErrorMessage('Please fill in all required fields for Order.');
      }
    } catch (error) {
      console.error('Error submitting order:', error);
      if (error.response?.data?.non_field_errors?.[0]?.includes('order_number, company must make a unique set')) {
        setErrorMessage('Order number already exists for your company.');
      } else if (error.response?.data?.non_field_errors?.[0]?.includes('work_permit_number, company must make a unique set')) {
        setErrorMessage('Work permit number already exists for your company.');
      } else {
        setErrorMessage(error.response?.data?.detail || 'Error updating or creating order.');
      }
    }
  };

  if (loading) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent>
          <p>Loading...</p>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[95vh] overflow-y-auto rounded-2xl">
        <DialogHeader className="bg-gradient-to-r from-brand-50 to-steel-50 -mx-6 -mt-6 px-6 py-4 rounded-t-2xl border-b border-steel-200">
          <DialogTitle className="text-lg font-semibold text-slate-900">{data ? 'Edit Order' : 'Create New Order'}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="mt-4 space-y-6">
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Company To</label>
              <Select
                options={companyOptions}
                value={companyOptions.find(option => option.value === orderData.companyToId) || null}
                onChange={handleCompanyChange}
                placeholder="Select company..."
                isClearable
                className="basic-single"
                classNamePrefix="select"
                required
                styles={customSelectStyles}
              />
            </div>

            {orderData.companyToId && (
              <>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Order Number</label>
                  <input
                    type="text"
                    value={orderData.orderNumber}
                    onChange={e => handleManualInputChange('orderNumber', e.target.value)}
                    className="w-full p-2 border border-steel-300 rounded-xl focus:outline-none focus:ring-1 focus:ring-brand-500"
                    placeholder="Enter order number"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Work Permit Number</label>
                  <input
                    type="text"
                    value={orderData.workPermitNumber}
                    onChange={e => handleManualInputChange('workPermitNumber', e.target.value)}
                    className="w-full p-2 border border-steel-300 rounded-xl focus:outline-none focus:ring-1 focus:ring-brand-500"
                    placeholder="Enter work permit number"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Turbine*</label>
                  <Select
                    options={turbineOptions}
                    value={turbineOptions.find(option => option.value === orderData.turbineId) || null}
                    onChange={option => handleManualInputChange('turbineId', option)}
                    placeholder="Select turbine..."
                    isClearable
                    className="basic-single"
                    classNamePrefix="select"
                    styles={customSelectStyles}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Scope of Work</label>
                  <CreatableSelect
                    options={scopeOptions}
                    value={
                      orderData.workScopeReal
                        ? { value: orderData.scopeOfWorkId, label: orderData.workScopeReal }
                        : scopeOptions.find(option => option.value === orderData.scopeOfWorkId) || null
                    }
                    onChange={option => handleManualInputChange('scopeOfWorkId', option)}
                    placeholder="Select or type a custom scope..."
                    isClearable
                    className="basic-single"
                    classNamePrefix="select"
                    formatCreateLabel={inputValue => `Use custom scope: "${inputValue}"`}
                    styles={customSelectStyles}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                  <textarea
                    value={orderData.comment}
                    onChange={e => handleManualInputChange('comment', e.target.value)}
                    className="w-full p-2 border border-steel-300 rounded-xl focus:outline-none focus:ring-1 focus:ring-brand-500"
                    placeholder="Enter description"
                    rows="4"
                  />
                </div>
              </>
            )}
          </div>

          {errorMessage && <p className="text-red-500 text-sm">{errorMessage}</p>}

          <DialogFooter className="border-t border-steel-200 -mx-6 -mb-6 px-6 py-4 bg-steel-50 rounded-b-2xl">
            <Button
              type="button"
              variant="outline"
              className="px-4 py-2 bg-white text-slate-700 rounded-full hover:bg-steel-100 border border-steel-300"
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="outline"
              className="px-4 py-2 bg-brand-500 text-white rounded-full hover:bg-brand-600 shadow-lg shadow-brand-500/20 disabled:opacity-50"
              disabled={loading || !canCreateOrder}
            >
              {data ? 'Update Order' : 'Create Order'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default OrderCreation;